import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { config } from '../../data/config';
import { getCookies } from 'cookies-next';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

interface FileItem {
    id: string;
    url: string;
    mimetype?: string;
    title?: string;
    size?: number;
}

interface FileUploaderProps {
    count?: number;
    id: string;
    component: string;
}

function getFileName(item: FileItem): string {
    if (item.title?.trim()) return item.title.trim();
    const segment = item.url?.split('/').pop();
    return segment ? decodeURIComponent(segment) : 'Document';
}

function getFileExtension(item: FileItem): string {
    const name = getFileName(item);
    const ext = name.split('.').pop();
    if (ext && ext !== name) return ext.toUpperCase();
    const mime = item.mimetype?.split('/').pop();
    return mime ? mime.toUpperCase() : 'FILE';
}

function formatFileSize(bytes?: number): string | null {
    if (bytes == null || Number.isNaN(bytes)) return null;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileTypeIcon({ mimetype, extension }: { mimetype?: string; extension: string }) {
    const isPdf = mimetype?.includes('pdf') || extension === 'PDF';
    const isWord =
        mimetype?.includes('word') ||
        mimetype?.includes('document') ||
        ['DOC', 'DOCX'].includes(extension);
    const isExcel =
        mimetype?.includes('sheet') ||
        mimetype?.includes('excel') ||
        ['XLS', 'XLSX', 'CSV'].includes(extension);

    const colorClass = isPdf
        ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
        : isWord
          ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
          : isExcel
            ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';

    return (
        <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${colorClass}`}>
            {isPdf ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                </svg>
            ) : (
                <span className="text-xs font-bold tracking-wide">{extension.slice(0, 4)}</span>
            )}
        </div>
    );
}

export default function FileUploader({ count = 40, id, component }: FileUploaderProps) {
    const { t } = useTranslation();
    const cookie = getCookies();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [listimage, setListimage] = useState<FileItem[]>([]);
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    const canUploadMore = listimage.length < count;

    const loaditem = async () => {
        setLoading(true);
        try {
            const response = await axios({
                method: 'get',
                url: `${config.url}/v1/dashboard/filemanager?id=${id}&component=${component}`,
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                },
            });
            setListimage(response.data.data || []);
        } catch (error) {
            console.error('Load documents failed:', error);
            Swal.fire({
                title: t('Error'),
                text: t('Failed to load documents'),
                icon: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const uploadFiles = async (files: FileList | File[]) => {
        const fileArray = Array.from(files);
        if (!fileArray.length) return;

        const remaining = count - listimage.length;
        if (remaining <= 0) {
            Swal.fire({
                title: t('Upload limit reached'),
                text: t('Maximum {{count}} documents allowed', { count }),
                icon: 'warning',
            });
            return;
        }

        const toUpload = fileArray.slice(0, remaining);
        if (toUpload.length < fileArray.length) {
            Swal.fire({
                title: t('Some files skipped'),
                text: t('Only {{count}} more document(s) can be uploaded', { count: remaining }),
                icon: 'info',
                timer: 2500,
                showConfirmButton: false,
            });
        }

        setUploading(true);
        try {
            for (const file of toUpload) {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('component', component);
                formData.append('parent', id);

                await axios({
                    method: 'post',
                    url: `${config.url}/v1/dashboard/filemanager/single`,
                    data: formData,
                    headers: {
                        Authorization: 'Bearer ' + cookie['token'],
                        'Content-Type': 'multipart/form-data',
                    },
                });
            }

            Swal.fire({
                title: t('Uploaded successfully'),
                icon: 'success',
                timer: 1200,
                showConfirmButton: false,
            });
            await loaditem();
        } catch (error) {
            console.error('Upload failed:', error);
            Swal.fire({
                title: t('Upload failed'),
                text: t('Please try again'),
                icon: 'error',
            });
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files?.length) return;
        await uploadFiles(files);
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        if (!canUploadMore || uploading) return;
        const files = e.dataTransfer.files;
        if (files?.length) await uploadFiles(files);
    };

    const deletedItem = async (myid: string) => {
        const result = await Swal.fire({
            title: t('Delete document?'),
            text: t('This action cannot be undone'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete'),
            cancelButtonText: t('Cancel'),
        });

        if (!result.isConfirmed) return;

        setDeletingId(myid);
        try {
            await axios({
                method: 'delete',
                url: `${config.url}/v1/dashboard/filemanager/${myid}`,
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                },
            });
            Swal.fire({
                title: t('Deleted'),
                icon: 'success',
                timer: 1200,
                showConfirmButton: false,
            });
            await loaditem();
        } catch (error) {
            console.error('Delete failed:', error);
            Swal.fire({
                title: t('Error'),
                text: t('Failed to delete document'),
                icon: 'error',
            });
        } finally {
            setDeletingId(null);
        }
    };

    useEffect(() => {
        if (id) loaditem();
    }, [id, component]);

    return (
        <div className="w-full" dir="rtl">
            {/* Upload zone */}
            {canUploadMore && (
                <div className="mb-6">
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={handleChange}
                        disabled={uploading}
                    />
                    <div
                        role="button"
                        tabIndex={0}
                        onClick={() => !uploading && fileInputRef.current?.click()}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (!uploading) fileInputRef.current?.click();
                            }
                        }}
                        onDragOver={(e) => {
                            e.preventDefault();
                            if (!uploading) setIsDragging(true);
                        }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={handleDrop}
                        className={`
                            w-full py-8 px-6 rounded-xl border-2 border-dashed transition-all duration-300
                            ${
                                isDragging
                                    ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 shadow-lg scale-[1.01]'
                                    : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 hover:border-amber-400 hover:bg-amber-50/30 dark:hover:bg-amber-900/10 hover:shadow-md'
                            }
                            ${uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                            flex flex-col items-center justify-center gap-3
                        `}
                    >
                        {uploading ? (
                            <>
                                <div className="bg-amber-100 dark:bg-amber-900/40 rounded-full p-3">
                                    <svg
                                        className="animate-spin h-6 w-6 text-amber-600 dark:text-amber-400"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        />
                                    </svg>
                                </div>
                                <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                                    {t('Uploading...')}
                                </span>
                            </>
                        ) : (
                            <>
                                <div
                                    className={`bg-amber-100 dark:bg-amber-900/40 rounded-full p-4 transition-colors duration-300 ${isDragging ? 'bg-amber-200 dark:bg-amber-900/60' : ''}`}
                                >
                                    <svg
                                        className="w-8 h-8 text-amber-600 dark:text-amber-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                        />
                                    </svg>
                                </div>
                                <div className="text-center">
                                    <span className="text-sm text-gray-700 dark:text-gray-300 font-medium block">
                                        {isDragging ? t('Drop files here') : t('Click or drop documents here')}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">
                                        {t('PDF, Word, Excel and other files')} · {listimage.length}/{count}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {!canUploadMore && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200 text-center">
                    {t('Maximum {{count}} documents reached', { count })}
                </div>
            )}

            {/* Document list */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <svg
                        className="animate-spin h-8 w-8 text-amber-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                        />
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                    </svg>
                </div>
            ) : listimage.length > 0 ? (
                <div className="space-y-3">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                        {t('Uploaded documents')} ({listimage.length})
                    </p>
                    {listimage.map((item, index) => {
                        const fileName = getFileName(item);
                        const extension = getFileExtension(item);
                        const sizeLabel = formatFileSize(item.size);
                        const downloadUrl = `${config.fronturl}/${item.url}`;

                        return (
                            <div
                                key={item.id}
                                className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200 group"
                            >
                                <FileTypeIcon mimetype={item.mimetype} extension={extension} />

                                <div className="flex-1 min-w-0 text-right">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={fileName}>
                                        {fileName}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex flex-wrap gap-x-2 justify-end">
                                        <span>{extension}</span>
                                        {item.mimetype && (
                                            <>
                                                <span aria-hidden>·</span>
                                                <span className="truncate max-w-[140px]">{item.mimetype}</span>
                                            </>
                                        )}
                                        {sizeLabel && (
                                            <>
                                                <span aria-hidden>·</span>
                                                <span>{sizeLabel}</span>
                                            </>
                                        )}
                                        <span aria-hidden>·</span>
                                        <span>#{index + 1}</span>
                                    </p>
                                </div>

                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <a
                                        href={downloadUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded-lg text-sm font-medium transition-colors"
                                        title={t('Download')}
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                            />
                                        </svg>
                                        <span className="hidden sm:inline">{t('Download')}</span>
                                    </a>
                                    <button
                                        type="button"
                                        onClick={() => deletedItem(item.id)}
                                        disabled={deletingId === item.id}
                                        className="flex items-center justify-center p-2 bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title={t('Delete')}
                                    >
                                        {deletingId === item.id ? (
                                            <svg
                                                className="animate-spin h-4 w-4"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                />
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                />
                                            </svg>
                                        ) : (
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="text-center py-10 text-gray-400 dark:text-gray-500">
                    <div className="flex flex-col items-center">
                        <svg className="w-12 h-12 mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                        </svg>
                        <p className="text-sm">{t('No documents uploaded yet')}</p>
                        <p className="text-xs mt-1 text-gray-400 dark:text-gray-500">
                            {t('Upload brochures, floor plans, or legal documents')}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
